import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, ShieldCheck } from "lucide-react";

export default function RecoverWithKey() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", recovery_key: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recover-with-key", {
        email: form.email,
        recovery_key: form.recovery_key.trim(),
        new_password: form.new_password,
      });
      setNewKey(data.new_recovery_key);
      toast.success("Password reset — please save your new recovery key");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not recover");
    } finally {
      setLoading(false);
    }
  };

  if (newKey) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-lg" data-testid="recover-success">
          <ShieldCheck size={48} strokeWidth={1} className="text-[#D4AF37]" />
          <h1 className="font-serif-h font-light text-4xl text-stone-900 mt-6">Password reset</h1>
          <p className="mt-4 text-stone-600 font-light text-sm leading-relaxed">
            Your old recovery key has been used. <b>Save this new recovery key</b> — it's the only way to recover your password if you forget it. We won't show it again.
          </p>
          <div className="mt-8 border border-warm bg-[#F8EDE9]/40 p-5 rounded-sm">
            <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-2">New Recovery Key</div>
            <div className="flex items-center gap-3">
              <code className="text-sm break-all flex-1 font-mono">{newKey}</code>
              <button onClick={() => { navigator.clipboard.writeText(newKey); toast("Copied"); }}
                      className="p-2 hover:text-[#D4AF37]" data-testid="copy-key">
                <Copy size={16} />
              </button>
            </div>
          </div>
          <Link to="/login" className="btn-ink mt-10 inline-flex">Sign In with new password</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md" data-testid="recover-page">
        <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Account Recovery</div>
        <h1 className="font-serif-h font-light text-4xl text-stone-900">Recover with key</h1>
        <p className="mt-4 text-stone-600 font-light text-sm leading-relaxed">
          Use your one-time recovery key to set a new password. A fresh key will be generated and shown to you on success — save it somewhere safe.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-7" data-testid="recover-form">
          <div>
            <label className="text-[11px] tracking-overline uppercase text-stone-500">Email</label>
            <input required type="email" className="input-line" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rec-email" />
          </div>
          <div>
            <label className="text-[11px] tracking-overline uppercase text-stone-500">Recovery Key</label>
            <input required className="input-line font-mono" value={form.recovery_key}
                   onChange={(e) => setForm({ ...form, recovery_key: e.target.value })} data-testid="rec-key" />
          </div>
          <div>
            <label className="text-[11px] tracking-overline uppercase text-stone-500">New Password</label>
            <input required type="password" minLength={6} className="input-line" value={form.new_password}
                   onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="rec-pw" />
          </div>
          <div>
            <label className="text-[11px] tracking-overline uppercase text-stone-500">Confirm Password</label>
            <input required type="password" minLength={6} className="input-line" value={form.confirm}
                   onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="rec-confirm" />
          </div>
          <button disabled={loading} className="btn-ink w-full justify-center disabled:opacity-50" data-testid="rec-submit">
            {loading ? "Recovering..." : "Set New Password"}
          </button>
        </form>
        <div className="mt-8 text-sm text-stone-600 text-center">
          <Link to="/login" className="text-[#D4AF37] hover:underline">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
